import React, { useEffect, useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlugZap, Hash, Briefcase, MessageSquare, CheckCircle2, AlertTriangle, Trash2, Send } from "lucide-react";
import LiveModeBadge from "@/components/LiveModeBadge";

const CHANNELS = [
  { k: "slack", label: "Slack", icon: Hash, kind: "webhook" },
  { k: "teams", label: "Microsoft Teams", icon: Briefcase, kind: "webhook" },
  { k: "twilio", label: "Twilio (SMS)", icon: MessageSquare, kind: "twilio" },
];

export default function SettingsIntegrations() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations");
      setList(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byKey = Object.fromEntries(list.map((x) => [x.channel, x]));

  return (
    <SettingsLayout>
      <div className="mb-6">
        <div className="eb-overline mb-2">Module 6.4 / 6.5 · Integrations</div>
        <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="integrations-heading">
          <PlugZap className="w-7 h-7 text-orange-500" />
          Integrations
        </h1>
        <p className="text-sm text-navy-500 mt-1">
          Configure Slack, Teams, and Twilio for arrival notifications. Webhook URLs are stored full server-side and shown masked here.
        </p>
        <div className="mt-3 flex gap-2 flex-wrap">
          <LiveModeBadge service="resend" />
          <LiveModeBadge service="twilio" />
        </div>
      </div>

      {loading ? (
        <p className="text-navy-500">Loading…</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHANNELS.map((c) => (
            <ChannelCard
              key={c.k}
              meta={c}
              cfg={byKey[c.k]}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </SettingsLayout>
  );
}

function ChannelCard({ meta, cfg, onChanged }) {
  const Ic = meta.icon;
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState("");
  const [twilio, setTwilio] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [busy, setBusy] = useState(false);

  const connected = !!cfg && cfg.active;

  const save = async () => {
    setBusy(true);
    try {
      if (meta.kind === "webhook") {
        if (!/^https?:\/\//.test(url)) throw new Error("URL must start with http(s)://");
        await api.post(`/integrations/${meta.k}`, { webhook_url: url });
      } else if (meta.kind === "twilio") {
        await api.post(`/integrations/${meta.k}`, twilio);
      }
      toast.success(`${meta.label} connected`);
      setEditing(false);
      setUrl(""); setTwilio({ account_sid: "", auth_token: "", from_number: "" });
      onChanged();
    } catch (e) { toast.error(formatApiError(e) || e.message); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/${meta.k}/test`);
      if (data.ok) toast.success(`${meta.label} test sent successfully`);
      else toast.error(`${meta.label} test failed (status ${data.status || "?"})`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${meta.label}?`)) return;
    try { await api.delete(`/integrations/${meta.k}`); toast.success("Disconnected"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="eb-card p-5 flex flex-col" data-testid={`integration-${meta.k}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy-700 flex items-center justify-center">
          <Ic className="w-5 h-5" />
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-success/15 text-success">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-navy-100 text-navy-500">
            Not connected
          </span>
        )}
      </div>
      <h3 className="mt-3 font-semibold text-navy-900">{meta.label}</h3>
      {connected && (
        <div className="text-xs text-navy-500 mt-1 space-y-0.5">
          {cfg?.config?.webhook_url && <div>URL: <code>{cfg.config.webhook_url}</code></div>}
          {cfg?.config?.from_number && <div>From: <code>{cfg.config.from_number}</code></div>}
          {cfg?.config?.account_sid && <div>SID: <code>{cfg.config.account_sid}</code></div>}
          {cfg?.last_test_at && <div>Last test: {new Date(cfg.last_test_at).toLocaleString()} ({cfg.last_test_status})</div>}
        </div>
      )}

      {editing && meta.kind === "webhook" && (
        <div className="mt-3 space-y-2">
          <input className="eb-input" placeholder="https://hooks.slack.com/..." value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`${meta.k}-webhook-input`} />
          <div className="text-[11px] text-navy-500 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-warning" /> We'll ping the webhook with a one-line test message before saving.
          </div>
        </div>
      )}
      {editing && meta.kind === "twilio" && (
        <div className="mt-3 space-y-2">
          <input className="eb-input" placeholder="Account SID" value={twilio.account_sid} onChange={(e) => setTwilio({ ...twilio, account_sid: e.target.value })} data-testid="twilio-sid" />
          <input className="eb-input" placeholder="Auth token" value={twilio.auth_token} onChange={(e) => setTwilio({ ...twilio, auth_token: e.target.value })} data-testid="twilio-token" />
          <input className="eb-input" placeholder="From number (+44...)" value={twilio.from_number} onChange={(e) => setTwilio({ ...twilio, from_number: e.target.value })} data-testid="twilio-from" />
          <div className="text-[11px] text-navy-500">Phase 2: Twilio runs in MOCK — no real SMS will be sent until live mode is flipped.</div>
        </div>
      )}

      <div className="mt-auto pt-4 flex gap-2">
        {!connected && !editing && (
          <button onClick={() => setEditing(true)} className="btn-accent text-xs" data-testid={`connect-${meta.k}`}>
            Connect
          </button>
        )}
        {editing && (
          <>
            <button onClick={save} disabled={busy} className="btn-accent text-xs" data-testid={`save-${meta.k}`}>
              {busy ? "Saving…" : "Save & test"}
            </button>
            <button onClick={() => setEditing(false)} className="btn-ghost text-xs">Cancel</button>
          </>
        )}
        {connected && !editing && (
          <>
            <button onClick={test} disabled={busy} className="btn-outline text-xs" data-testid={`test-${meta.k}`}>
              <Send className="w-3 h-3" /> {busy ? "Sending…" : "Send test"}
            </button>
            <button onClick={() => setEditing(true)} className="btn-ghost text-xs">Replace</button>
            <button onClick={disconnect} className="btn-ghost text-danger text-xs ml-auto" data-testid={`disconnect-${meta.k}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
